// Éventail d'ascendance (fan chart) en SVG natif : anneaux concentriques, un
// secteur d'arc par ancêtre, angle 180° (demi-cercle) ou 360° (cercle plein).
// forwardRef sur le <svg> : l'export (exportTree.ts) a besoin de l'élément DOM.

import React, { forwardRef } from 'react';
import { Membre, Relation } from '../../types';
import { getAncestors, AncestorNode } from '../../utils/lineage';
import {
  StylePreset, sectorPath, polar, resolveBg, memberYears, accentStroke,
} from './chartCommon';

export interface FanChartProps {
  rootId: number;
  membres: Membre[];
  relations: Relation[];
  style: StylePreset;
  isDark: boolean;
  maxGen: number;
  angle: 180 | 360;
}

interface Wedge { node: AncestorNode; a0: number; a1: number; }

const RING_W = 62;

const FanChart = forwardRef<SVGSVGElement, FanChartProps>(function FanChart(
  { rootId, membres, relations, style, isDark, maxGen, angle }, ref,
) {
  const root = getAncestors(rootId, membres, relations, maxGen);
  const full360 = angle === 360;
  const A0 = full360 ? -90 : 180;
  const A1 = full360 ? 270 : 360;
  const R = (maxGen + 1) * RING_W;
  const pad = 16;
  const cx = R + pad;
  const cy = R + pad;
  const W = 2 * (R + pad);
  const H = full360 ? 2 * (R + pad) : R + 2 * pad;

  // Répartition angulaire récursive : père = première moitié, mère = seconde.
  const wedges: Wedge[] = [];
  (function assign(node: AncestorNode | null, a0: number, a1: number) {
    if (!node) return;
    wedges.push({ node, a0, a1 });
    const mid = (a0 + a1) / 2;
    if (node.pere || node.mere) {
      assign(node.pere, a0, mid);
      assign(node.mere, mid, a1);
    }
  })(root, A0, A1);

  const shapes: React.ReactNode[] = [];
  const labels: React.ReactNode[] = [];
  const accent = accentStroke(style);

  wedges.forEach(({ node, a0, a1 }, i) => {
    const g = node.gen;
    const ri = g === 0 ? 0 : g * RING_W;
    const ro = (g + 1) * RING_W;
    const m: Membre = node.membre;
    const key = `w${i}`;
    // La personne racine (gen 0) est mise en avant par un liseré d'accent.
    const isRoot = g === 0;
    const stroke = isRoot ? accent : style.sectorStroke;
    const strokeW = isRoot ? 2.5 : style.sectorStrokeW;

    if (isRoot && full360) {
      // Arc plein 360° dégénéré (angle départ = fin) → cercle explicite.
      shapes.push(
        <circle key={key} cx={cx} cy={cy} r={ro}
          fill={style.sectorFill(0)} stroke={stroke} strokeWidth={strokeW} />,
      );
    } else {
      shapes.push(
        <path key={key} d={sectorPath(cx, cy, ri, ro, a0, a1)}
          fill={style.sectorFill(g)} stroke={stroke} strokeWidth={strokeW} />,
      );
    }

    const am = (a0 + a1) / 2;
    const rm = g === 0 ? 0 : (ri + ro) / 2;
    const [tx, ty] = g === 0 ? [cx, cy - (full360 ? 6 : -2)] : polar(cx, cy, rm, am);
    const sub = memberYears(m);

    if (g <= 1) {
      labels.push(
        <text key={`${key}n`} x={tx} y={ty - 4} fill={style.sectorText} fontFamily={style.font}
          fontSize={g === 0 ? 15 : 12.5} fontWeight={style.weight} textAnchor="middle">{m.prenom}</text>,
        <text key={`${key}s`} x={tx} y={ty + (g === 0 ? 13 : 11)} fill={style.sectorText} fontFamily={style.font}
          fontSize={g === 0 ? 11 : 9.5} fontWeight={style.weightSub} textAnchor="middle"
          fontStyle={style.italicSub ? 'italic' : undefined} opacity={0.9}>
          {`${m.nom}${sub ? ` · ${sub}` : ''}`}
        </text>,
      );
    } else {
      // Texte radial maintenu à l'endroit
      let rot = am;
      if (rot > 90 && rot < 270) rot += 180;
      const fs = g >= 4 ? 8.5 : 10;
      labels.push(
        <g key={`${key}g`} transform={`translate(${tx} ${ty}) rotate(${rot})`}>
          <text fill={style.sectorText} fontFamily={style.font} fontSize={fs}
            fontWeight={style.weight} textAnchor="middle" dy={-1}>{m.prenom}</text>
          {sub && (
            <text fill={style.sectorText} fontFamily={style.font} fontSize={fs - 1.5}
              fontWeight={style.weightSub} textAnchor="middle" dy={fs} opacity={0.85}
              fontStyle={style.italicSub ? 'italic' : undefined}>{sub}</text>
          )}
        </g>,
      );
    }
  });

  const bg = resolveBg(style, isDark);

  return (
    <svg ref={ref} width={Math.ceil(W)} height={Math.ceil(H)} viewBox={`0 0 ${Math.ceil(W)} ${Math.ceil(H)}`}
      xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Éventail d'ascendance">
      <rect x={0} y={0} width={Math.ceil(W)} height={Math.ceil(H)} fill={bg} />
      {shapes}
      {labels}
    </svg>
  );
});

export default FanChart;
